/**
 * Utility functions for building and parsing URL query parameters
 * Automatically handles URL encoding and filters out undefined/null values
 */

type QueryParamValue = string | number | boolean | undefined | null;
type QueryParams = Record<string, QueryParamValue>;

/**
 * Builds a query string from an object of parameters
 * Automatically encodes values and filters out undefined/null
 *
 * @example
 * buildQueryString({ order: '123', brand: 'Test & Co' })
 * // Returns: "order=123&brand=Test%20%26%20Co"
 */
export const buildQueryString = (params: QueryParams): string => {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    // Skip undefined and null values
    if (value !== undefined && value !== null) {
      searchParams.append(key, String(value));
    }
  });

  return searchParams.toString();
};

/**
 * Builds a complete URL with base path and query parameters
 * Handles URLs that already have query parameters
 *
 * @example
 * buildUrlWithParams('/success', { order: '123', brand: 'Test' })
 * // Returns: "/success?order=123&brand=Test"
 *
 * buildUrlWithParams('https://example.com?existing=1', { new: '2' })
 * // Returns: "https://example.com?existing=1&new=2"
 */
export const buildUrlWithParams = (baseUrl: string, params: QueryParams): string => {
  const queryString = buildQueryString(params);
  if (!queryString) return baseUrl;

  // Check if baseUrl already has query parameters
  const hasQueryParams = baseUrl.includes('?');
  const separator = hasQueryParams ? '&' : '?';

  return `${baseUrl}${separator}${queryString}`;
};

/**
 * Parses query string into an object
 *
 * @example
 * parseQueryString('order=123&brand=Test%20%26%20Co')
 * // Returns: { order: '123', brand: 'Test & Co' }
 */
export const parseQueryString = (queryString: string): Record<string, string> => {
  const params = new URLSearchParams(queryString);
  const result: Record<string, string> = {};

  params.forEach((value, key) => {
    result[key] = value;
  });

  return result;
};

/**
 * Parses query parameters from a full URL
 *
 * @example
 * parseUrlParams('https://example.com/path?order=123&brand=Test')
 * // Returns: { order: '123', brand: 'Test' }
 */
export const parseUrlParams = (url: string): Record<string, string> => {
  try {
    const urlObj = new URL(url);
    return parseQueryString(urlObj.search.substring(1));
  } catch {
    // If URL parsing fails, try to extract query string manually
    const queryStart = url.indexOf('?');
    if (queryStart === -1) return {};
    return parseQueryString(url.substring(queryStart + 1));
  }
};
